import { useMemo } from "react";

import { sortBondRows } from "../../SHARED/bondPageUtils";

/** Sort, pagination window, rows, and column config for law-firm bonds / team-underwriting lists. */
export default function useLawFirmBondsTable({
  filteredData,
  allBonds,
  isTeamMember,
  page,
  rowsPerPage,
  orderBy,
  order,
  handleSort,
}) {
  const searchQuery = "";

  const pendingCount = useMemo(() => {
    if (!isTeamMember) return 0;
    return allBonds.filter(
      (b) => ((b.underwriting_status || "").toString().toLowerCase()) === "pending"
    ).length;
  }, [isTeamMember, allBonds]);

  const noPendingBonds = useMemo(() => {
    if (!isTeamMember) return true;
    return allBonds.every(
      (b) => ((b.underwriting_status || "").toString().toLowerCase()) !== "pending"
    );
  }, [isTeamMember, allBonds]);

  const archiveBondNumberOptions = useMemo(() => {
    if (!isTeamMember) return [];
    return [...new Set(allBonds.map((b) => b.bond_number).filter(Boolean))].map((num) => ({
      label: num,
      value: num,
    }));
  }, [isTeamMember, allBonds]);

  const filteredRows = useMemo(
    () =>
      sortBondRows(
        filteredData.filter((r) => {
          if (!searchQuery.trim()) return true;
          const query = searchQuery.toLowerCase();
          const statusField = isTeamMember
            ? r.underwriting_status?.toLowerCase()
            : r.status?.toLowerCase();
          return (
            r.principals.some((p) => p.toLowerCase().includes(query)) ||
            r.bond_number?.toLowerCase().includes(query) ||
            r.estate_name?.toLowerCase().includes(query) ||
            r.law_firm_name?.toLowerCase().includes(query) ||
            r.attorney_name?.toLowerCase().includes(query) ||
            statusField?.includes(query)
          );
        }),
        orderBy,
        order
      ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, isTeamMember, order, orderBy, page, rowsPerPage, searchQuery]
  );

  const tableColumns = isTeamMember
    ? [
        "bond_number",
        "bond_type",
        "bond_sub_category",
        "principals",
        "estate_name",
        "law_firm_name",
        "attorney_name",
        "bond_amount",
        "underwriting_status",
        "created_at",
      ]
    : [
        "bond_number",
        "bond_type",
        "bond_sub_category",
        "principals",
        "estate_name",
        "law_firm_name",
        "attorney_name",
        "bond_amount",
        "status",
        "created_at",
      ];

  const tableColSpan = tableColumns.length + 1;

  return {
    filteredRows,
    tableColumns,
    tableColSpan,
    pendingCount,
    noPendingBonds,
    archiveBondNumberOptions,
    handleSort,
  };
}
